/**
 * Shared bits used across the support widget's tabs/views — status pill,
 * lane chip, search box, article list rows, centered loading state, and the
 * pending-attachment chip (shared between SupportComposer and ComposeView so
 * the two composer surfaces can't drift). Centralized so the queue pages
 * (Task #4) can reuse the same tone mapping instead of re-deriving it.
 */

import styled, { css } from 'styled-components';
import { Loader2, Paperclip, Search, X } from 'lucide-react';
import type { SupportLane, SupportRequestStatus } from '@upskill/shared';
import type { PendingAttachment } from './useSupportAttachments';

export const STATUS_LABEL: Record<SupportRequestStatus, string> = {
  SUBMITTED: 'Submitted',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
};

export const LANE_LABEL: Record<SupportLane, string> = {
  ACCOUNT: 'Org Admin',
  PLATFORM: 'UpskillABA Support',
};

const statusTone = {
  SUBMITTED: css`
    background: ${({ theme }) => theme.colors.status.info.bg};
    color: ${({ theme }) => theme.colors.status.info.text};
  `,
  IN_PROGRESS: css`
    background: ${({ theme }) => theme.colors.status.warning.bg};
    color: ${({ theme }) => theme.colors.status.warning.text};
  `,
  RESOLVED: css`
    background: ${({ theme }) => theme.colors.status.success.bg};
    color: ${({ theme }) => theme.colors.status.success.text};
  `,
};

export const StatusPill = styled.span<{ $status: SupportRequestStatus }>`
  display: inline-block;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  white-space: nowrap;
  ${({ $status }) => statusTone[$status]}
`;

export const LaneChip = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  padding: 2px 7px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

export const UnreadDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary.sky};
  margin-top: 6px;
`;

export const CenteredState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[8]};
`;

// ── Help search box (HomeTab + HelpTab) ─────────────────────────────────────

export const SearchWrapper = styled.div`
  position: relative;
`;

export const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.light};
  width: 16px;
  height: 16px;
`;

export const SearchInput = styled.input`
  width: 100%;
  border: 1.5px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[2]} 36px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background: ${({ theme }) => theme.colors.backgrounds.white};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

export const SectionHead = styled.div`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: ${({ theme }) => theme.spacing[5]} 0 ${({ theme }) => theme.spacing[2]};
`;

export const ArticleItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

// ── Pending attachment chip (SupportComposer + ComposeView) ────────────────

const AttachmentChipEl = styled.div<{ $error?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${({ theme, $error }) =>
    $error ? theme.colors.status.error.bg : theme.colors.backgrounds.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.error.text : theme.colors.text.secondary};

  svg.spin {
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const RemoveChipButton = styled.button`
  display: inline-flex;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.light};

  &:hover {
    color: ${({ theme }) => theme.colors.status.error.text};
  }
`;

export interface PendingAttachmentChipProps {
  attachment: PendingAttachment;
  onRemove: (clientId: string) => void;
}

export function PendingAttachmentChip({ attachment, onRemove }: PendingAttachmentChipProps) {
  return (
    <AttachmentChipEl $error={attachment.error}>
      {attachment.uploading ? <Loader2 size={10} className="spin" /> : <Paperclip size={10} />}
      {attachment.file.name} ({(attachment.file.size / 1024).toFixed(0)} KB)
      {attachment.error && ' — failed'}
      <RemoveChipButton type="button" onClick={() => onRemove(attachment.clientId)}>
        <X size={10} />
      </RemoveChipButton>
    </AttachmentChipEl>
  );
}
